import { maskValue } from '../../../../helpers/maskValue/maskValue';
import { GetValueReturn, OnInputChangeProps, OnInputChangeReturn, SetValueProps } from '../../../../context/Keyboard/keyboard.types';
import { useLocale } from '../../../../context/Localization/localization';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { ChangeEvent, memo, useEffect, useRef, FocusEvent } from 'react';
import { useKeyboardIntegration } from '../../../../context/Keyboard/keyboard';
import { useField, useFormikContext } from 'formik';
import { KioskFormVinFieldProps } from '../FormVinField.types';
import { TextFieldValidation } from '../../../../helpers/FormFieldValidation';
import { useScrollToOnError } from '../../../../helpers/useScrollTo';

export const KioskFormVinField = memo(
    ({
        name,
        id,
        title,
        label,
        ariaLabel,
        disabled,
        required,
        autoFocus,
        keyboardLayout,
        shiftAfterSpace,
        shiftAfterPeriod,
        shiftAfterAmpersand,
        hideKeyboardOnClickOut,
        capsLock,
        fullWidth,
        seventyFivePercentWidth,
        initiallyMasked,
        tabFocusRefContext,
        onChangeCallback,
        vinType,
    }: KioskFormVinFieldProps) => {
        const [field, meta] = useField<string>({
            name: name,
            validate: TextFieldValidation({
                required: required ?? false,
                label,
                name,
                validationType: vinType,
            }),
        });
        const { submitCount, setFieldValue } = useFormikContext();

        /**
         * A ref to the HTMLInputElement in the MUI TextField.
         */
        const inputRef = useRef<HTMLInputElement>(null);
        /**
         * The focus ref to prevent a stale state when the on-screen keyboard
         * calls any functions that need to know if the field is focused or not.
         */
        const isFocused = useRef<boolean>(false);
        /**
         * The input value ref to prevent a stale state when the on-screen keyboard
         * calls any functions that use the internal value state.
         *
         * This also handles initial masking of the VIN Field, which should be handled by the
         * react-imask.
         */
        const valueRef = useRef<string>(initiallyMasked && !isFocused.current ? maskValue(field.value) : field.value ?? '');
        /**
         * The caret position ref to prevent a stale state when the on-screen keyboard
         * calls any functions that use the internal caret position state.
         */
        const caretPositionRef = useRef<number | null>(null);
        useScrollToOnError({
            scrollable: inputRef,
            name: name,
        });

        const {
            dealer: { primaryLanguage },
        } = useKioskState();
        const locale = useLocale();

        useEffect(() => {
            if (tabFocusRefContext) {
                tabFocusRefContext.current = inputRef.current;
            }
        }, [tabFocusRefContext]);

        useEffect(() => {
            /**
             * Updates the internal caret position when the internal MUI value finishes changing from the
             * pattern mask.
             */
            if (inputRef.current) {
                inputRef.current.setSelectionRange(caretPositionRef.current, caretPositionRef.current);
            }
        }, [inputRef.current?.value]);

        useEffect(() => {
            if (field.value !== valueRef.current) {
                valueRef.current = field.value;

                onChangeCallback && onChangeCallback({
                    target: {
                        value: valueRef.current,
                    },
                } as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>);
            }

        }, [field.value, onChangeCallback]);

        useEffect(() => {
            if (!disabled && inputRef.current) {
                isFocused.current = false;
            }
        }, [disabled]);

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const getValue = (): GetValueReturn => {
            if (inputRef.current && inputRef.current.selectionStart !== null && inputRef.current.selectionEnd !== null) {
                const selectionStart: number = inputRef.current.selectionStart;
                const selectionEnd: number = inputRef.current.selectionEnd;

                return {
                    inputValue: valueRef.current,
                    caretPositionStart: selectionStart,
                    caretPositionEnd: selectionEnd,
                };
            }
            else {
                return {
                    inputValue: valueRef.current,
                    caretPositionStart: valueRef.current.length,
                    caretPositionEnd: valueRef.current.length,
                };
            }
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const setValue = ({ value, caretPosition }: SetValueProps): void => {
            caretPositionRef.current = caretPosition;
            setFieldValue(name, value);
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const onInputChange = ({ value, caretPosition, wasBackspace, wasSelectionRange }: OnInputChangeProps): OnInputChangeReturn => {
            /**
             * When the value exceeds the length of a vin (length of 17 characters), we cut out
             * the most recent character and remove it. Then we set the caret position back one space.
             */
            if (value.length > 17) {
                value = value.slice(0, caretPosition - 1) + value.slice(caretPosition);
                caretPosition = caretPosition - 1;
            }

            return {
                value: value,
                caretPosition: caretPosition,
            };
        };

        /**
         * Used by the keyboard context and is described on the declaration there.
         */
        const onKeyboardInput = (): void => {
            if (inputRef.current && !isFocused.current) {
                inputRef.current.focus();
            }
        };

        const { onFocus, onBlur } = useKeyboardIntegration({
            fieldValue: field.value,
            getValue: getValue,
            setValue: setValue,
            onInputChange: onInputChange,
            onKeyboardInput: onKeyboardInput,
            keyboardLayout: keyboardLayout,
            shiftAfterPeriod: shiftAfterPeriod,
            shiftAfterAmpersand: shiftAfterAmpersand,
            shiftAfterSpace: shiftAfterSpace,
            hideKeyboardOnClickOut: hideKeyboardOnClickOut,
            capsLock: capsLock,
        });

        const handleOnBlur = () => {
            onBlur();
            isFocused.current = false;
        };

        const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            caretPositionRef.current = event.target.selectionEnd;
            setFieldValue(name, event.target.value);
        };

        const handleOnFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            /**
             * This handles unmasking the vin when the vin is masked.
             */
            if (valueRef.current.indexOf('*') !== -1) {
                valueRef.current = '';
                setFieldValue(name, '');
            }

            isFocused.current = true;
            onFocus(event);
        };

        return (
            <BaseTextField
                name={name}
                id={id}
                title={title}
                label={label}
                ariaLabel={ariaLabel}
                inputMode={'text'}
                type={'text'}
                inputRef={inputRef}
                value={valueRef.current}
                error={meta.error}
                touched={meta.touched || submitCount > 0}
                disabled={disabled}
                required={required}
                autoFocus={autoFocus}
                fullWidth={fullWidth}
                seventyFivePercentWidth={seventyFivePercentWidth}
                capsLock={capsLock}
                onFocus={handleOnFocus}
                onChange={handleOnChange}
                onBlur={handleOnBlur}
                safariHack={locale === primaryLanguage ? 'primarylang' : 'alternatelang'}
                inputProps={{
                    maxLength: 17,
                }}
            />
        );
    },
);
